import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState, useContext } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { formatCurrency } from '../utilities/general-utils'
import {
    RCFormProvider,
    FFText,
    FFDate,
    FFToggle,
    FFSelect,
    FFDayGridSelector,
    useRCFormContext
} from '@jlafica/rcform';
import FiltersHeader from '../components/FiltersHeader/FiltersHeader'
import UpcomingTransactions from '../components/UpcomingTransactions/UpcomingTransactions'
import { getRecurrenceDisplayString } from '../utilities/general-utils'
import { InputVarsContext } from '../contexts/input-vars-context'
import { svc_getAccounts } from "../services/account.service"
import { svc_getTransaction, svc_deleteAllExceptionsByTransactionId, svc_updateTransaction, svc_deleteTransactionPattern, svc_deleteTransaction } from "../services/transaction.service"
import { normalizeRawTransaction } from "../utilities/data-normalization";
import { IconAngleLeftBold } from "../assets/icons";
import {
    fieldNames,
    initialState,
    getTransactionSubmissionModel,
    getTransactionRawModelFromFieldValues,
    getTransactionFormFieldsModel,
    dayOfWeekFieldOptions
} from '../models/TransactionModel'
import { useResponseMessages } from "../hooks/useResponseMessage";

const TransactionEdit = ({ transactionItemId }) => {
    const { getAccessTokenSilently, user } = useAuth0();
    const navigate = useNavigate();
    const { vars } = useContext(InputVarsContext)
    const userId = user.sub
    let { id: transactionId } = useParams()
    const [formRef, setFormRef] = useState(null);
    const initialEditState = { ...initialState }
    const [isEditing, setIsEditing] = useState(false)
    const [transactionRaw, setTransactionRaw] = useState(null)
    const [hasExceptions, setHasExceptions] = useState(false)
    const [accounts, setAccounts] = useState(null)
    const [originalRecurrenceDisplayString, setOriginalRecurrenceDisplayString] = useState('')
    const [recurrenceDisplayString, setRecurrenceDisplayString] = useState('')
    const { addMessage } = useResponseMessages();
    const [dayOfWeekOptions, setDayOfWeekOptions] = useState([
        ...dayOfWeekFieldOptions.weekly
    ]);
    const [isFetching, setIsFetching] = useState(true)

    if (!transactionId && transactionItemId) {
        transactionId = transactionItemId
    }

    const getAccounts = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data } = await svc_getAccounts(accessToken, userId);

        if (data) {
            let accountOptions = data.map(account => {
                return {
                    value: account.id,
                    label: account.name
                }
            })
            setAccounts(accountOptions)
        }
    }

    const displayRecurrenceMessage = (formState, updateOriginal) => {
        const transactionEditObject = getTransactionRawModelFromFieldValues(transactionId, formState);
        setRecurrenceDisplayString(getRecurrenceDisplayString(transactionEditObject))
        if (!originalRecurrenceDisplayString || updateOriginal) {
            setOriginalRecurrenceDisplayString(getRecurrenceDisplayString(transactionEditObject))
        }
    }

    const handleDeleteExceptions = async (e) => {
        e.preventDefault()

        let confirmDelete = window.confirm(
            'Are you sure you want to delete all exceptions for this transaction?'
        )

        if (confirmDelete) {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await svc_deleteAllExceptionsByTransactionId(accessToken, userId, transactionId);

            if (data) {
                addMessage({
                    data: 'Exceptions successfully deleted'
                })
                setHasExceptions(false)
            }

            if (error) {
                addMessage({
                    error: 'Error deleting exceptions'
                })
            }
        }
    }

    const getTransaction = async (updateFields = true) => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await svc_getTransaction(accessToken, userId, transactionId);
        const transaction = data[0]
        setIsFetching(false)

        if (error || !transaction) {
            addMessage({
                error: [`No transaction found matching ID ${transactionId}`, error?.message]
            })
            return
        }

        const normalizedTransaction = normalizeRawTransaction(transaction)
        setTransactionRaw(transaction)

        if (updateFields) {
            const fetchedValues = getTransactionFormFieldsModel(normalizedTransaction);
            formRef?.updateFieldValues(fetchedValues)
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        const submissionObject = getTransactionSubmissionModel(transactionId, formRef?.formState)
        const currentFormState = formRef?.validateForm(formRef?.formState)

        if (!currentFormState.isValid) {
            addMessage({
                error: 'Please correct the errors in the form'
            })
            return
        }

        const accessToken = await getAccessTokenSilently();

        const { data, error } = await svc_updateTransaction(accessToken, userId, submissionObject);

        if (data) {
            addMessage({
                data: 'Transaction successfully updated'
            })
            setTimeout(() => {
                navigate('/transaction/' + transactionId)
            }, 300)
        }

        if (error) {
            addMessage({
                error: error.message
            })
        }
    }

    const handleDelete = async (e) => {
        e.preventDefault()

        let confirmDelete = window.confirm(
            'Are you sure you want to delete this transaction?'
        )

        if (confirmDelete) {
            const accessToken = await getAccessTokenSilently();

            const { 
                data : exceptionsDeletedResponse,
                error : exceptionsDeletedError
             } = await svc_deleteAllExceptionsByTransactionId(accessToken, userId, transactionId);

            const {
                data : patternDeletedResponse,
                error : patternDeletedError
            } = await svc_deleteTransactionPattern(accessToken, userId, transactionId);

            const {
                data : transactionDeletedResponse,
                error : transactionDeletedError
            } = await svc_deleteTransaction(accessToken, userId, transactionId);

            if (exceptionsDeletedError || patternDeletedError || transactionDeletedError) {
                addMessage({
                    error: 'Error deleting transaction'
                })
                return
            }

            if (exceptionsDeletedResponse && patternDeletedResponse && transactionDeletedResponse) {
                addMessage({
                    data: 'Transaction successfully deleted'
                })
                navigate('/transactions')
            }
        }
    }

    useEffect(() => {
        if (isFetching && vars.dateRange.startDate && vars.dateRange.endDate && formRef?.formState.hasAppliedInitialValues && !transactionRaw) {
            getTransaction()
            setIsFetching(false)
        }

        if (!accounts) {
            getAccounts()
        }
    }, [isFetching, vars.dateRange.startDate, vars.dateRange.endDate, accounts, formRef?.formState.hasAppliedInitialValues])

    useEffect(() => {
        if(formRef?.formState?.fields) {
            displayRecurrenceMessage(formRef?.formState)
        }
    }, [
        formRef?.formState.fields[fieldNames.isRecurring].checked,
        formRef?.formState.fields[fieldNames.recurringType].value,
        formRef?.formState.fields[fieldNames.dayOfWeek].value,
        formRef?.formState.fields[fieldNames.dayOfMonthList].selectedValues,
        formRef?.formState.fields[fieldNames.weekOfMonth].value,
        formRef?.formState.fields[fieldNames.monthOfYear].value,
        formRef?.formState.fields[fieldNames.dateFrequencyType].value,
        formRef?.formState.fields[fieldNames.separationCount].value
    ])

    return (
        <div className="content-width-container">
            <Link to="/transactions" className="button button-slim button-blue spc-m-b-1 spc-m-t-1">
                <IconAngleLeftBold fill="#fff" /> Back to My Transactions
            </Link>

            {transactionRaw && (
                <div className="text-center">
                    <h1 className="spc-m-t-0">{transactionRaw.title}</h1>

                    <h4 className="spc-m-t-0">
                        {formatCurrency(transactionRaw.amount)}
                    </h4>

                    {originalRecurrenceDisplayString && (
                        <p>
                            {originalRecurrenceDisplayString}
                        </p>
                    )}

                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className={`button spc-m-b-1 dis-block-xs ${isEditing ? "button-stroke" : "button-green"}`}
                    >
                        {isEditing ? 'Close Editor' : 'Click to Edit'}
                    </button>
                </div>
            )}

            <div className="standardFormWrap">
                <RCFormProvider
                    onSubmit={handleSubmit}
                    id="updateTransactionForm"
                    className={`formStandard ${!formRef?.formState?.isValid ? 'errors' : ''}`}
                    initialFieldState={initialEditState}
                    setFormRef={setFormRef}
                >
                    <FormContent
                        handleDelete={handleDelete}
                        handleDeleteExceptions={handleDeleteExceptions}
                        hasExceptions={hasExceptions}
                        accounts={accounts}
                        fieldNames={fieldNames}
                        recurrenceDisplayString={recurrenceDisplayString}
                        isEditing={isEditing}
                        dayOfWeekOptions={dayOfWeekOptions}
                        setDayOfWeekOptions={setDayOfWeekOptions}
                    />
                </RCFormProvider>
            </div>

            {transactionRaw && (
                <>
                    <div className="standardFormWrap">
                        <h4 className="spc-m-t-0 text-center">
                            Enter a Date Range
                        </h4>

                        <p className="text-center">
                            Modify the start and end date to view upcoming transactions within the selected range
                        </p>

                        <FiltersHeader
                            hideActiveFundsWidget={true}
                            isLoading={false}
                        />
                    </div>

                    <UpcomingTransactions
                        transaction={transactionRaw}
                        setHasExceptions={setHasExceptions}
                        userId={userId}
                    />
                </>
            )}
        </div>
    )
}

const FormContent = ({
    handleDelete,
    handleDeleteExceptions,
    hasExceptions,
    accounts,
    fieldNames,
    recurrenceDisplayString,
    isEditing,
    dayOfWeekOptions,
    setDayOfWeekOptions
}) => {
    const formContext = useRCFormContext()

    useEffect(() => {
        if (formContext.formState.fields[fieldNames.isRecurring].checked) {
            if (
                formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
            ) {
                setDayOfWeekOptions([...dayOfWeekFieldOptions.monthly])
            } else if (formContext.formState.fields[fieldNames.recurringType].value === 'weekly') {
                setDayOfWeekOptions([...dayOfWeekFieldOptions.weekly])
            }
        }
    }, [
        formContext.formState.fields[fieldNames.isRecurring].checked,
        formContext.formState.fields[fieldNames.recurringType].value
    ])

    // Reset conditional weekly and monthly fields if date frequency type changes
    const handleDateFrequencyTypeChange = (e) => {
        let fieldsToReset = {
            [fieldNames.dayOfMonthList]: { selectedValues: [] },
            [fieldNames.dayOfWeek]: { value: null },
            [fieldNames.weekOfMonth]: { value: null }
        }

        if (e.target.name === fieldNames.dateFrequencyType) {
            fieldsToReset[fieldNames.dateFrequencyType] = { value: e.target.value }
        }

        if (e.target.name === fieldNames.recurringType) {
            fieldsToReset[fieldNames.recurringType] = { value: e.target.value }
        }

        formContext.updateFieldValues(fieldsToReset)
    }

    return (
        <>
            <>
                <FFText
                    fieldName={fieldNames.title}
                    label="Title*"
                    wrapperModifierClass={"formField"}
                    required={true}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFText
                    fieldName={fieldNames.description}
                    label="Description"
                    wrapperModifierClass={"formField"}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFText
                    type="number"
                    label="Amount*"
                    fieldName={fieldNames.amount}
                    wrapperModifierClass={"formField"}
                    required={true}
                    additionalProps={{
                        step: .01
                    }}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFText
                    label="Category*"
                    fieldName={fieldNames.category}
                    wrapperModifierClass={"formField"}
                    required={true}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFText
                    label="Subcategory"
                    fieldName={fieldNames.subcategory}
                    wrapperModifierClass={"formField"}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFSelect
                    label="Debit Account*"
                    fieldName={fieldNames.account}
                    options={[
                        {value: '', label: ''},
                        ...(accounts ? accounts : [])
                    ]}
                    required={true}
                    wrapperModifierClass={"formField"}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFDate
                    label="Start Date*"
                    fieldName={fieldNames.startDate}
                    required={true}
                    wrapperModifierClass={"formField"}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFDate
                    label="End Date"
                    fieldName={fieldNames.endDate}
                    wrapperModifierClass={"formField"}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                    pattern={null}
                />

                <FFToggle
                    label="Recurrence"
                    buttonOnValue="Recurring"
                    buttonOffValue="One-Time"
                    fieldName={fieldNames.isRecurring}
                    wrapperModifierClass="formField"
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />
            </>

            {/* Recurring Fields */}

            <div style={{
                display: formContext.formState.fields[fieldNames.isRecurring].checked ? 'block' : 'none'
            }}>
                <FFSelect
                    label="Frequency"
                    fieldName={fieldNames.recurringType}
                    options={[
                        { value: '', label: 'Select a frequency option' },
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                    wrapperModifierClass={`formField`}
                    required={formContext.formState.fields[fieldNames.isRecurring].checked}
                    customErrorMessage={"This field is required for recurring transactions"}
                    onChange={handleDateFrequencyTypeChange}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFSelect
                    label="Month of Year*"
                    fieldName={fieldNames.monthOfYear}
                    formatValue={parseInt}
                    options={[
                        { value: '', label: 'Select a Month' },
                        { value: 0, label: 'January' },
                        { value: 1, label: 'February' },
                        { value: 2, label: 'March' },
                        { value: 3, label: 'April' },
                        { value: 4, label: 'May' },
                        { value: 5, label: 'June' },
                        { value: 6, label: 'July' },
                        { value: 7, label: 'August' },
                        { value: 8, label: 'September' },
                        { value: 9, label: 'October' },
                        { value: 10, label: 'November' },
                        { value: 11, label: 'December' },
                    ]}
                    displayCondition={formContext.formState.fields[fieldNames.recurringType].value === 'yearly'}
                    required={formContext.formState.fields[fieldNames.recurringType].value === 'yearly'}
                    wrapperModifierClass={`formField`}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <FFSelect
                    label="Date Frequency*"
                    fieldName={fieldNames.dateFrequencyType}
                    options={[
                        { value: '', label: '' },
                        { value: 1, label: 'Specific date(s)' },
                        { value: 2, label: 'Nth occurrence' },
                    ]}
                    displayCondition={
                        formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                        formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                    }
                    required={
                        formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                        formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                    }
                    wrapperModifierClass={`formField`}
                    onChange={handleDateFrequencyTypeChange}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                />

                <div
                    className={
                        (
                            formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                            formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                        ) &&
                        parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                            ? 'formField-row'
                            : ''
                    }
                >
                    <FFSelect
                        label="Nth day*"
                        fieldName={fieldNames.weekOfMonth}
                        options={[
                            { value: '',       label: ''     },
                            { value: '1',      label: '1st'  },
                            { value: '2',      label: '2nd'  },
                            { value: '3',      label: '3rd'  },
                            { value: '4',      label: '4th'  },
                            { value: '5',      label: '5th'  },
                            { value: 'last',   label: 'Last' }
                        ]}
                        wrapperModifierClass={`formField`}
                        displayCondition={
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                            ) &&
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                        }
                        required={
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                            ) &&
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                        }
                        readOnly={!isEditing}
                        disabled={!isEditing}
                        />

                    <FFSelect
                        label="Day of Week*"
                        fieldName={fieldNames.dayOfWeek}
                        formatValue={parseInt}
                        options={dayOfWeekOptions}
                        displayCondition={(
                            formContext.formState.fields[fieldNames.recurringType].value === 'weekly' ||
                            (
                                (
                                    formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                    formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                                ) &&
                                parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                            )
                        )}
                        required={(
                            formContext.formState.fields[fieldNames.recurringType].value === 'weekly' ||
                            (
                                (
                                    formContext.formState.fields[fieldNames.recurringType].value === 'monthly' ||
                                    formContext.formState.fields[fieldNames.recurringType].value === 'yearly'
                                ) &&
                                parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 2
                            )
                        )}
                        wrapperModifierClass={`formField`}
                        readOnly={!isEditing}
                        disabled={!isEditing}
                        />
                </div>

                <div>
                    <FFDayGridSelector
                        label="Day(s) of Month*"
                        fieldName={fieldNames.dayOfMonthList}
                        displayCondition={
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 1 &&
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly'
                            )
                        }
                        required={
                            parseInt(formContext.formState.fields[fieldNames.dateFrequencyType].value) === 1 &&
                            (
                                formContext.formState.fields[fieldNames.recurringType].value === 'yearly' ||
                                formContext.formState.fields[fieldNames.recurringType].value === 'monthly'
                            )
                        }
                        wrapperModifierClass={`formField`}
                        readOnly={!isEditing}
                        disabled={!isEditing}
                        />
                </div>

                <FFText
                    type="number"
                    label="Occurrence Interval*"
                    fieldName={fieldNames.separationCount}
                    formatValue={(val) => {return val !== '' ? parseInt(val) : val}}
                    additionalProps={{
                        min: 1
                    }}
                    wrapperModifierClass={`formField`}
                    readOnly={!isEditing}
                    disabled={!isEditing}
                    required={formContext.formState.fields[fieldNames.isRecurring].checked}
                />
            </div>

            {formContext.formState.fields[fieldNames.isRecurring].checked && (
                <div>
                    <h5 className="spc-m-t-1 spc-m-b-half">Frequency Result:</h5>
                    <p>{recurrenceDisplayString}</p>
                </div>
            )}

            <div className="formField button-row">
                <button
                    type="submit"
                    className="button button-green dis-block-xs"
                    disabled={!formContext.fieldValuesHaveChanged(formContext.formState)}
                >
                    Apply Updates
                </button>

                <button
                    className="button button-red dis-block-xs"
                    onClick={handleDelete}
                >
                    Delete Transaction{hasExceptions && " & Exceptions"} 
                </button>

                {hasExceptions && (
                    <button
                        className="button button-red dis-block-xs"
                        onClick={handleDeleteExceptions}
                    >
                        Delete Exceptions Only
                    </button>
                )}
            </div>
        </>
    )
}

export default TransactionEdit
